/**
 * Why provider-api refused to send a signing link.
 *
 * The endpoint answers 409 to three unrelated questions — this order cannot be
 * signed in the app at all, this order has no envelope awaiting signature *yet*,
 * and a link has already gone out — and the status alone cannot tell them apart.
 * Only `already-sent` changes what the clinician is told, because it is the one
 * refusal that still means an email exists.
 *
 * `not-ready` is now rare rather than routine. provider-api records an ask made
 * before the envelope exists and sends it once the envelope arrives, so the
 * common early click is accepted outright; this value survives for deployments
 * with no claims table to record it in.
 *
 * Reading the endpoint's prose to separate them is a stopgap, for the same
 * reason it is a stopgap in `signingMintRetry`: a reason code on the response is
 * the real fix and it belongs in provider-api. Until then the matching is kept
 * here, in one place, rather than spread through the page.
 *
 * Unrecognised text resolves to `not-ready`. That is the fail-safe direction —
 * an unknown refusal becomes an honest error, whereas guessing `already-sent`
 * tells the clinician an email is coming when none is.
 */

/**
 * - `already-sent` — a link was genuinely issued; a second one will not be.
 * - `not-ready` — no envelope to link to, and nowhere to hold the ask.
 * - `not-eligible` — this order is not on the embedded channel.
 */
export type ReminderRefusal = 'already-sent' | 'not-ready' | 'not-eligible'

const ALREADY_SENT = 'already been sent'
const NOT_ELIGIBLE = 'not enabled for in-app signing'

export const reminderRefusalFromDetail = (detail: string | null | undefined): ReminderRefusal => {
  const text = detail?.toLowerCase() ?? ''
  if (text.includes(ALREADY_SENT)) return 'already-sent'
  if (text.includes(NOT_ELIGIBLE)) return 'not-eligible'
  return 'not-ready'
}
